import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider, createTheme, StyledEngineProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Layout from './components/Layout';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';

// Pages
import { LoginPage as Login } from './pages/authentication/LoginPage';
import Dashboard from './pages/Dashboard';
import CarMasters from './pages/CarMasters';
import CustomerMasters from './pages/CustomerMasters';
import CustomerDetails from './pages/CustomerDetails';
import Bookings from './pages/Bookings';
import Services from './pages/Services';
import ServiceManagement from './pages/service-management';
import ServiceList from './pages/service-management/ServiceList';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import AddonServiceList from './pages/addon-services/AddonServiceList';
import AddonServiceForm from './pages/addon-services/AddonServiceForm';
import CleaningEquipmentList from './pages/cleaning-equipment/CleaningEquipmentList';
import CleaningEquipmentForm from './pages/cleaning-equipment/CleaningEquipmentForm';
import CustomerRequirementsList from './pages/customer-requirements/CustomerRequirementsList';
import CustomerRequirementsForm from './pages/customer-requirements/CustomerRequirementsForm';
import StaffManagement from './pages/staff-management';
import StaffDetails from './pages/staff-management/StaffDetails';
import BookingDetails from './pages/BookingDetails';
import { FAQList, FAQForm } from './pages/faq';
import PaymentTransactions from './pages/payment-transactions';
import CancellationPolicies from './pages/CancellationPolicies';
import Vouchers from './pages/Vouchers';
import Commissions from './pages/commissions';
import Payouts from './pages/Payouts';
import Banners from './pages/Banners';
import ProtectedRoute from './components/ProtectedRoute';
import Pages from './pages/Pages';
import AdminList from './pages/admin-management/AdminList';
import SalesRevenueReport from './pages/SalesRevenueReport';
import ServicePerformanceReport from './pages/ServicePerformanceReport';
import CustomerFeedbackReport from './pages/CustomerRatings';
import CustomerReports from './pages/CustomerReports';
import StaffReport from './pages/StaffReport';
import CarBrands from './pages/car-masters/CarBrands';
import CarModels from './pages/car-masters/CarModels';

// Define color constants
const colors = {
  // Primary colors
  primary: '#4d8eff',
  primaryLight: 'rgba(77, 142, 255, 0.1)',
  primaryDark: '#3a6bc8',
  primaryHover: 'rgba(77, 142, 255, 0.15)',
  
  // Background colors
  bgDark: '#1a1d24',
  bgDarker: '#14171d',
  bgLight: '#242a34',
  bgLighter: '#2d333b',
  
  // Text colors
  textPrimary: '#e0e0e0',
  textSecondary: '#a0a0a0',
  textDisabled: '#6c757d',
  
  // Border & Divider
  border: '#2d333b',
  divider: 'rgba(255, 255, 255, 0.12)',
  
  // Status colors
  success: '#4caf50',
  warning: '#ff9800',
  error: '#f44336',
  info: '#2196f3',
};

const darkTheme = createTheme({
  components: {
    MuiCssBaseline: {
      defaultProps: {
        enableColorScheme: true,
      },
      styleOverrides: (theme) => ({
        html: {
          fontSize: '16px',
          '@media (max-width: 900px)': {
            fontSize: '15px',
          },
          '@media (max-width: 600px)': {
            fontSize: '14px',
          },
        },
        body: {
          backgroundColor: colors.bgDark,
          color: colors.textPrimary,
          fontFeatureSettings: '"calt" 1, "case" 1, "dlig" 1, "cv01" 1, "cv02" 1, ' +
            '"cv03" 1, "cv04" 1, "cv09" 1',
          textRendering: 'optimizeLegibility',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          lineHeight: 1.6,
        },
        a: {
          color: colors.primary,
          textDecoration: 'none',
          transition: 'color 0.2s ease, opacity 0.2s ease',
          '&:hover': {
            textDecoration: 'underline',
            opacity: 0.9,
          },
        },
        'h1, h2, h3, h4, h5, h6': {
          fontWeight: 600,
          lineHeight: 1.2,
          letterSpacing: '-0.02em',
          margin: '0 0 1rem',
          color: colors.textPrimary,
          fontFeatureSettings: '"ss01", "ss02", "calt" 1',

        },
        p: {
          margin: '0 0 1.5rem',
          lineHeight: 1.7,
          fontSize: '1rem',
          color: 'rgba(255, 255, 255, 0.85)',
          '&:last-child': {
            marginBottom: 0,
          },
        },
        'input, textarea, select, button': {
          fontFamily: '"Inter", sans-serif',
          fontSize: '1rem',
          lineHeight: 1.5,
        },
      }),
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'h1',
          h2: 'h2',
          h3: 'h3',
          h4: 'h4',
          h5: 'h5',
          h6: 'h6',
          subtitle1: 'h3',
          subtitle2: 'h4',
          body1: 'p',
          body2: 'span',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          letterSpacing: '0.01em',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
          },
        },
        contained: {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: colors.bgLight,
          color: colors.textPrimary,
          border: `1px solid ${colors.border}`,
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: colors.bgDarker,
          color: colors.textPrimary,
          borderBottom: `1px solid ${colors.border}`,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          zIndex: theme.zIndex.drawer + 1,
          '& .MuiToolbar-root': {
            minHeight: '64px',
            padding: '0 24px',
            '@media (min-width: 600px)': {
              minHeight: '64px',
            },
          },
        }),
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          '& .MuiTypography-h6': {
            fontSize: '1.25rem',
            fontWeight: 600,
            lineHeight: 1.6,
            letterSpacing: '0.0075em',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.bgDarker,
          color: colors.textPrimary,
          borderRight: `1px solid ${colors.border}`,
          width: '260px',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          margin: '0.25rem 0.5rem',
          padding: '0.5rem 1rem',
          '&.Mui-selected': {
            backgroundColor: colors.primaryLight,
            color: colors.primary,
            '&:hover': {
              backgroundColor: colors.primaryHover,
            },
            '& .MuiListItemIcon-root': {
              color: colors.primary,
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: '40px',
          color: colors.textSecondary,
        },
      },
    },
  },
  palette: {
    mode: 'dark',
    primary: {
      main: colors.primary,
      light: colors.primaryLight,
      dark: colors.primaryDark,
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.primary, // Using primary as secondary for now
      light: colors.primaryLight,
      dark: colors.primaryDark,
      contrastText: '#ffffff',
    },
    background: {
      default: colors.bgDark,
      paper: colors.bgLight,
    },
    text: {
      primary: colors.textPrimary,
      secondary: colors.textSecondary,
      disabled: colors.textDisabled,
    },
    divider: colors.divider,
    error: {
      main: colors.error,
    },
    warning: {
      main: colors.warning,
    },
    info: {
      main: colors.info,
    },
    success: {
      main: colors.success,
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    fontSize: 15,
    htmlFontSize: 15,
    // Font weights
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    // Ensure all text uses Inter
    allVariants: {
      fontFamily: '"Inter", sans-serif',
      letterSpacing: '-0.01em',
    },
    // Headings
    h1: {
      fontWeight: 800, // Extra bold for main headings
      fontSize: '2.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
      margin: '0 0 1.5rem',
      color: colors.textPrimary,
      '@media (min-width: 600px)': {
        fontSize: '3rem',
      },
      '@media (min-width: 1200px)': {
        fontSize: '3.5rem',
      },
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.25,
      letterSpacing: '-0.015em',
      margin: '0 0 1.25rem',
      color: colors.textPrimary,
      '@media (min-width: 600px)': {
        fontSize: '2.5rem',
      },
    },
    h3: {
      fontWeight: 700,
      fontSize: '1.75rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
      margin: '0 0 1rem',
      color: colors.textPrimary,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.35,
      letterSpacing: '-0.008em',
      margin: '0 0 0.75rem',
      color: colors.textPrimary,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
      letterSpacing: '-0.005em',
      margin: '0 0 0.5rem',
      color: colors.textPrimary,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.1rem',
      lineHeight: 1.5,
      letterSpacing: '0.0025em',
      margin: '0 0 0.5rem',
      textTransform: 'none',
      color: colors.textPrimary,
    },
    // Subtitles and body text
    subtitle1: {
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.7,
      letterSpacing: '0.01em',
      color: 'rgba(255, 255, 255, 0.85)',
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: '0.9rem',
      lineHeight: 1.6,
      letterSpacing: '0.006em',
      color: 'rgba(255, 255, 255, 0.75)',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.7,
      letterSpacing: '0.01em',
      color: 'rgba(255, 255, 255, 0.9)',
      fontWeight: 400,
    },
    body2: {
      fontSize: '0.9rem',
      lineHeight: 1.7,
      letterSpacing: '0.01em',
      color: 'rgba(255, 255, 255, 0.8)',
      fontWeight: 400,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.9375rem',
      lineHeight: 1.5,
      letterSpacing: '0.02em',
      fontFeatureSettings: '"tnum" 1, "calt" 1',
    },
    caption: {
      fontSize: '0.8rem',
      lineHeight: 1.5,
      letterSpacing: '0.02em',
      color: 'rgba(255, 255, 255, 0.7)',
      fontWeight: 400,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      lineHeight: 2,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: 'rgba(255, 255, 255, 0.6)',
      display: 'inline-block',
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
  shadows: [
    'none',
    '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
  ],
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },
  zIndex: {
    mobileStepper: 1000,
    speedDial: 1050,
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },
});

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#696cff',
      light: 'rgba(105, 108, 255, 0.1)',
      dark: '#5568d3',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#696cff',
      light: 'rgba(105, 108, 255, 0.1)',
      dark: '#5568d3',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
    text: {
      primary: '#212529',
      secondary: '#6c757d',
      disabled: '#adb5bd',
    },
    divider: 'rgba(0, 0, 0, 0.12)',
    error: {
      main: '#f44336',
    },
    warning: {
      main: '#ff9800',
    },
    info: {
      main: '#2196f3',
    },
    success: {
      main: '#4caf50',
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    fontSize: 15,
    htmlFontSize: 15,
    // Font weights
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    // Ensure all text uses Inter
    allVariants: {
      fontFamily: '"Inter", sans-serif',
      letterSpacing: '-0.01em',
    },
    // Headings
    h1: {
      fontWeight: 800,
      fontSize: '2.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
      margin: '0 0 1.5rem',
      color: '#212529',
      '@media (min-width: 600px)': {
        fontSize: '3rem',
      },
      '@media (min-width: 1200px)': {
        fontSize: '3.5rem',
      },
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.25,
      letterSpacing: '-0.015em',
      margin: '0 0 1.25rem',
      color: '#212529',
      '@media (min-width: 600px)': {
        fontSize: '2.5rem',
      },
    },
    h3: {
      fontWeight: 700,
      fontSize: '1.75rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
      margin: '0 0 1rem',
      color: '#212529',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.35,
      letterSpacing: '-0.008em',
      margin: '0 0 0.75rem',
      color: '#212529',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
      letterSpacing: '-0.005em',
      margin: '0 0 0.5rem',
      color: '#212529',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.1rem',
      lineHeight: 1.5,
      letterSpacing: '0.0025em',
      margin: '0 0 0.5rem',
      textTransform: 'none',
      color: '#212529',
    },
    // Subtitles and body text
    subtitle1: {
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.7,
      letterSpacing: '0.01em',
      color: '#495057',
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: '0.9rem',
      lineHeight: 1.6,
      letterSpacing: '0.006em',
      color: '#6c757d',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.7,
      letterSpacing: '0.01em',
      color: '#212529',
      fontWeight: 400,
    },
    body2: {
      fontSize: '0.9rem',
      lineHeight: 1.7,
      letterSpacing: '0.01em',
      color: '#6c757d',
      fontWeight: 400,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.9375rem',
      lineHeight: 1.5,
      letterSpacing: '0.02em',
      fontFeatureSettings: '"tnum" 1, "calt" 1',
    },
    caption: {
      fontSize: '0.8rem',
      lineHeight: 1.5,
      letterSpacing: '0.02em',
      color: '#6c757d',
      fontWeight: 400,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      lineHeight: 2,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: '#6c757d',
      display: 'inline-block',
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
  shadows: [
    'none',
    '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  ],
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },
  zIndex: {
    mobileStepper: 1000,
    speedDial: 1050,
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },
});

const MuiThemedContent = ({ children }: { children: React.ReactNode }) => {
  const { isDarkMode } = useTheme();
  const theme = isDarkMode ? darkTheme : lightTheme;
  
  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
};

function AppRoutes() {
  const location = useLocation();
  const hideLayout = location.pathname === '/' || location.pathname === '/login';

  return (
    <MuiThemedContent>
      {hideLayout ? (
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<Login />} />
        </Routes>
      ) : (
        <Layout>
          <Routes>
            {/* Protected Routes */}
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/car-masters" element={<ProtectedRoute><CarMasters /></ProtectedRoute>} />

            {/* ✅ Customer Routes */}
            <Route path="/customer-masters" element={<ProtectedRoute><CustomerMasters /></ProtectedRoute>} />
            {/* <Route path="/customers/:id" element={<ProtectedRoute><CustomerDetails /></ProtectedRoute>} /> */}
            <Route path="/customers" element={<CustomerMasters />} />
             <Route path="/customers/:id" element={<CustomerDetails />} />

            {/* ✅ Service Management */}
            <Route path="/services" element={<ProtectedRoute><ServiceList /></ProtectedRoute>} />
            <Route path="/services/new" element={<ProtectedRoute><ServiceManagement /></ProtectedRoute>} />
            <Route path="/services/edit/:serviceId" element={<ProtectedRoute><ServiceManagement /></ProtectedRoute>} />
            <Route path="/service-management/:serviceId" element={<ProtectedRoute><ServiceManagement /></ProtectedRoute>} />
            <Route path="/services/categories" element={<ProtectedRoute><Services /></ProtectedRoute>} />

            {/* ✅ Addon Services */}
            <Route path="/addon-services" element={<ProtectedRoute><AddonServiceList /></ProtectedRoute>} />
            <Route path="/addon-services/new" element={<ProtectedRoute><AddonServiceForm /></ProtectedRoute>} />
            <Route path="/addon-services/edit/:id" element={<ProtectedRoute><AddonServiceForm /></ProtectedRoute>} />

            {/* ✅ Cleaning Equipment */}
            <Route path="/cleaning-equipment" element={<ProtectedRoute><CleaningEquipmentList /></ProtectedRoute>} />
            <Route path="/cleaning-equipment/new" element={<ProtectedRoute><CleaningEquipmentForm /></ProtectedRoute>} />
            <Route path="/cleaning-equipment/edit/:id" element={<ProtectedRoute><CleaningEquipmentForm /></ProtectedRoute>} />

            {/* ✅ Customer Requirements */}
            <Route path="/customer-requirements" element={<ProtectedRoute><CustomerRequirementsList /></ProtectedRoute>} />
            <Route path="/customer-requirements/new" element={<ProtectedRoute><CustomerRequirementsForm /></ProtectedRoute>} />
            <Route path="/customer-requirements/edit/:id" element={<ProtectedRoute><CustomerRequirementsForm /></ProtectedRoute>} />

            {/* ✅ Staff, Bookings, Reports, etc. */}
            <Route path="/staff-management" element={<ProtectedRoute><StaffManagement /></ProtectedRoute>} />
            <Route path="/bookings" element={<ProtectedRoute><Bookings /></ProtectedRoute>} />
            <Route path="/bookings/:id" element={<BookingDetails />} />

            <Route path="/banners" element={<ProtectedRoute><Banners /></ProtectedRoute>} />
            
            <Route path="/washers" element={<StaffManagement />} />
            <Route path="/washers/:id" element={<StaffDetails />} />
            <Route path="/vouchers" element={<ProtectedRoute><Vouchers /></ProtectedRoute>} />
            <Route path="/faqs" element={<ProtectedRoute><FAQList /></ProtectedRoute>} />
            <Route path="/faqs/new" element={<ProtectedRoute><FAQForm /></ProtectedRoute>} />
            <Route path="/faqs/edit/:id" element={<ProtectedRoute><FAQForm /></ProtectedRoute>} />
            <Route path="/payment-transactions" element={<ProtectedRoute><PaymentTransactions /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/cancellation-policies" element={<ProtectedRoute><CancellationPolicies /></ProtectedRoute>} />
            <Route path="/commissions" element={<ProtectedRoute><Commissions /></ProtectedRoute>} />
            <Route path="/payouts" element={<ProtectedRoute><Payouts /></ProtectedRoute>} />
            <Route path="/pages" element={<Pages />} />
            <Route path="/reports/sales-revenue" element={<SalesRevenueReport />} />
            <Route path="/reports/service-performance" element={<ServicePerformanceReport />} />
            <Route path="/reports/customer-feedback" element={<CustomerFeedbackReport />} />
            <Route path="/reports/customer-reports" element={<CustomerReports />} />
            <Route path="/reports/staff" element={<StaffReport />} />
            <Route path="/car-brands" element={<CarBrands />} />
            <Route path="/car-models" element={<CarModels />} />

            {/* ✅ Admin Management */}
            <Route path="/admin-management" element={<ProtectedRoute><AdminList /></ProtectedRoute>} />

            {/* ✅ Fallback route 
            <Route path="*" element={<Navigate to="/dashboard" replace />} />*/}
          </Routes>
        </Layout>
      )}
    </MuiThemedContent>
  );
}

function App() {
  return (
    <ThemeProvider>
      <StyledEngineProvider injectFirst>
        <Router>
          <AppRoutes />
        </Router>
      </StyledEngineProvider>
    </ThemeProvider>
  );
}

export default App;
